import { request, requestMultipart } from '@/api/client';
import type {
  CompanyDraft,
  CreateRegistrationRequest,
  CreateRegistrationResponse,
  FinishRegistrationResponse,
  LookupUidRequest,
  LookupUidResponse,
  RegistrationClient,
  RegistrationStateResponse,
  ResendCodeResponse,
  ResendSmsCodeRequest,
  SubmitCompanyRequest,
  SubmitGoalRequest,
  SubmitPackageRequest,
  SubmitPackageResponse,
  SubmitProfileRequest,
  UploadLogoResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  VerifyMobileRequest,
  VerifyMobileResponse,
} from '@/types/registration';

const BASE = '/v1/account/registration';

// the account step shows conflicts inline, so the default toast would duplicate them
const QUIET_ACCOUNT_OPTIONS = { skipDefaultErrorHandler: true };

// the verify-email step shows code and resend errors inline
const QUIET_VERIFY_OPTIONS = { skipDefaultErrorHandler: true };

// the mobile card shows its failures inline and branches on the error code itself
const QUIET_MOBILE_OPTIONS = { skipDefaultErrorHandler: true };

// the company step translates per-field codes inline; the generic top-level message is untranslated
const QUIET_COMPANY_OPTIONS = { skipDefaultErrorHandler: true };

// the completion screen shows a duplicate-company refusal in its own banner
const QUIET_FINISH_OPTIONS = { skipDefaultErrorHandler: true };

/** maps the wire shape to `CompanyDraft` */
function mapCompanyDraft(response: any): CompanyDraft {
  return {
    name: response.name,
    uid: response.uid,
    careersPage: response.careers_page ?? '',
    industry: response.industry,
    companySize: response.company_size,
    street: response.street,
    postcode: response.postcode,
    city: response.city,
    invoiceEmail: response.invoice_email ?? '',
    billingSameAsCompany: response.billing_same_as_company,
    billingStreet: response.billing_street ?? '',
    billingPostcode: response.billing_postcode ?? '',
    billingCity: response.billing_city ?? '',
    logoId: response.logo_id,
    logoUrl: response.logo_url ?? null,
    brandColors: response.brand_colors,
  };
}

/**
 * `create`, `verifyEmail` and `resendEmailCode` run anonymously; everything else runs after the
 * registrant is signed in at email verification. Wire snake_case is mapped to camelCase here.
 * Screens catch `ClientError` and read `responseJson.code` themselves.
 */
export const registrationClient: RegistrationClient = {
  create(body: CreateRegistrationRequest): Promise<CreateRegistrationResponse> {
    return request(
      'POST',
      BASE,
      {},
      {
        email: body.email,
        mobile: body.mobile,
        mobile_country: body.mobileCountry,
        password: body.password,
        accepted_terms: body.acceptedTerms,
        honeypot: body.honeypot ?? '',
      },
      QUIET_ACCOUNT_OPTIONS
    ).then((response) => ({
      email: response.email,
      maskedMobile: response.masked_mobile,
      expiresInSeconds: response.expires_in,
      resendAfterSeconds: response.resend_after,
    }));
  },

  verifyEmail(body: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    return request('POST', `${BASE}/verification/email/check`, {}, { code: body.code }, QUIET_VERIFY_OPTIONS).then(
      (response) => ({
        step: response.step,
        email: response.email,
        emailVerified: true as const,
      })
    );
  },

  resendEmailCode(): Promise<ResendCodeResponse> {
    return request('POST', `${BASE}/verification/email/resend`, {}, {}, QUIET_VERIFY_OPTIONS).then((response) => ({
      expiresInSeconds: response.expires_in,
      resendAfterSeconds: response.resend_after,
    }));
  },

  verifyMobile(body: VerifyMobileRequest): Promise<VerifyMobileResponse> {
    return request('POST', `${BASE}/verify_mobile`, {}, { code: body.code }, QUIET_MOBILE_OPTIONS).then((response) => ({
      mobileVerified: response.data.mobile_verified,
    }));
  },

  resendSmsCode(body: ResendSmsCodeRequest): Promise<ResendCodeResponse> {
    return request('POST', `${BASE}/resend_sms_code`, {}, body, QUIET_MOBILE_OPTIONS).then((response) => ({
      expiresInSeconds: response.data.expires_in,
      resendAfterSeconds: response.data.resend_after,
      maskedMobile: response.data.masked_mobile,
    }));
  },

  requestVoiceCode(): Promise<ResendCodeResponse> {
    return request('POST', `${BASE}/request_voice_code`, {}, {}, QUIET_MOBILE_OPTIONS).then((response) => ({
      expiresInSeconds: response.data.expires_in,
      resendAfterSeconds: response.data.resend_after,
    }));
  },

  lookupUid(body: LookupUidRequest): Promise<LookupUidResponse> {
    // flat body, not wrapped under `data`; a blocking outcome throws a translated 400
    return request(
      'POST',
      `${BASE}/lookup_uid`,
      {},
      { uid: body.uid, name: body.name ?? '' },
      QUIET_COMPANY_OPTIONS
    ).then((response) => ({
      available: response.available,
      verified: response.verified,
      companyName: response.company_name ?? undefined,
      street: response.street ?? undefined,
      postcode: response.postcode ?? undefined,
      city: response.city ?? undefined,
      message: response.message,
    }));
  },

  uploadLogo(file: File, pixelDensity?: string | number): Promise<UploadLogoResponse> {
    const formData = new FormData();
    formData.append('image', file);

    // reuses the authenticated media endpoint, class 2 = logo
    const params: { [key: string]: any } = { class: 2 };
    if (pixelDensity) params.pixel_density = pixelDensity;

    return requestMultipart('POST', '/v1/account/media/images/upload', params, formData).then((response) => ({
      id: response.data.id,
      url: response.data.proxy_url,
    }));
  },

  submitProfile(body: SubmitProfileRequest): Promise<void> {
    return request(
      'PUT',
      `${BASE}/profile`,
      {},
      {
        first_name: body.firstName,
        last_name: body.lastName,
        job_title: body.jobTitle,
      }
    ).then(() => undefined);
  },

  getState(): Promise<RegistrationStateResponse> {
    return request('GET', BASE, {}).then((response) => ({
      step: response.step,
      emailVerified: response.email_verified,
      mobileVerified: response.mobile_verified,
      email: response.email,
      maskedMobile: response.masked_mobile,
      submitted: {
        profile: {
          firstName: response.submitted.profile.first_name,
          lastName: response.submitted.profile.last_name,
          jobTitle: response.submitted.profile.job_title,
          preferredLanguage: '',
        },
        company: mapCompanyDraft(response.submitted.company),
        goal: {
          mainGoal: response.submitted.goal.main_goal,
          rolesPerYear: response.submitted.goal.roles_per_year,
          whenToStart: response.submitted.goal.hiring_start_window,
        },
        package: {
          campaignMode: response.submitted.package.campaign_mode,
        },
      },
    }));
  },

  submitCompany(body: SubmitCompanyRequest): Promise<void> {
    return request(
      'PUT',
      `${BASE}/company`,
      {},
      {
        name: body.name,
        careers_page: body.careersPage,
        industry: body.industry,
        company_size: body.companySize,
        street: body.street,
        postcode: body.postcode,
        city: body.city,
        invoice_email: body.invoiceEmail,
        billing_street: body.billingStreet,
        billing_postcode: body.billingPostcode,
        billing_city: body.billingCity,
        logo_id: body.logoId,
        brand_colors: body.brandColors,
        uid: body.uid,
      },
      QUIET_COMPANY_OPTIONS
    ).then(() => undefined);
  },

  skipGoal(): Promise<void> {
    return request('PUT', `${BASE}/goal`, {}, { skip: true }).then(() => undefined);
  },

  submitGoal(body: SubmitGoalRequest): Promise<void> {
    return request(
      'PUT',
      `${BASE}/goal`,
      {},
      {
        main_goal: body.mainGoal,
        roles_per_year: body.rolesPerYear,
        hiring_start_window: body.whenToStart,
      }
    ).then(() => undefined);
  },

  submitPackage(body: SubmitPackageRequest): Promise<SubmitPackageResponse> {
    return request('PUT', `${BASE}/package`, {}, { campaign_mode: body.campaignMode }).then((response) => ({
      featureSetupWarning: response.feature_setup_warning != null,
    }));
  },

  finish(): Promise<FinishRegistrationResponse> {
    return request('POST', `${BASE}/finish`, {}, {}, QUIET_FINISH_OPTIONS).then((response) => ({
      customerId: response.data.customer_id,
      companyId: response.data.company_id,
      accountId: response.data.account_id,
    }));
  },

  setPassword(password: string): Promise<void> {
    return request('PUT', `${BASE}/password`, {}, { password }).then(() => undefined);
  },
};
